import { Injectable, inject } from '@angular/core';
import { Theme } from '../../backend/model/theme.model';
import { ThemeService } from '../../backend/api';
import { AuthenticationContextDatasourceService } from '../../backend/datasource';
import { BehaviorSubject, Observable, of, switchMap, tap } from 'rxjs';

@Injectable()
export class ThemeHandlerService {
  private themeService = inject(ThemeService);
  private authenticationContextDatasource = inject(AuthenticationContextDatasourceService);

  private themeSubject = new BehaviorSubject<Theme | null>(null);
  public theme$ = this.themeSubject.asObservable();

  private root = document.documentElement;
  private themeElement: HTMLLinkElement | null = null;

  public fetchTheme(): Observable<Theme> {
    return this.theme$.pipe(
      switchMap(theme => {
        if (theme) {
          return of(theme);
        } else {
          return this.themeService
            .getTheme(
              this.authenticationContextDatasource.getSessionId(),
              this.authenticationContextDatasource.getIdp()
            )
            .pipe(
              tap(fetchedTheme => {
                fetchedTheme.value.value = JSON.parse(fetchedTheme.value.value);
                this.themeSubject.next(fetchedTheme);
                this.setThemeCss(fetchedTheme.value.value.theme.mainTheme);
                this.setPrimaryColor(fetchedTheme.value.value.theme.primaryColor);
                this.setSecondaryColor(fetchedTheme.value.value.theme.secondaryColor);
              })
            );
        }
      })
    );
  }

  getTheme(): Observable<any> {
    return this.themeSubject.asObservable();
  }

  setThemeCss(theme: string): void {
    let themeElement = document.getElementById('theme-link') as HTMLLinkElement;
    if (!themeElement) {
      themeElement = document.createElement('link');
      themeElement.id = 'theme-link';
      themeElement.rel = 'stylesheet';
      document.head.appendChild(themeElement);
    }
    themeElement.href = `assets/themes/${theme.toLowerCase()}-tokens.css`;

    const currentClasses = this.root.className;
    const modeMatch: string[] = currentClasses.match(/ids--\w+/g) || [];
    const mode = modeMatch.find(cls => cls.includes('light') || cls.includes('dark')) || 'ids--light';

    const newClasses = currentClasses
      .replace(/ids--[\w-]+(?=\s|$)/g, '')
      .trim()
      .split(/\s+/)
      .filter((cls, idx, arr) => idx === arr.indexOf(cls));

    newClasses.unshift(`ids--${theme.toLowerCase()}`, mode);
    this.root.className = newClasses.join(' ');
  }

  setPrimaryColor(primaryColor: string): void {
    this.root.style.setProperty('--primary-color', primaryColor);
  }

  setSecondaryColor(secondaryColor: string): void {
    this.root.style.setProperty('--secondary-color', secondaryColor);
  }
}
